"use client";

import { c as _c } from "react/compiler-runtime";
import { endOfDay, startOfDay } from "date-fns";
import { SyntheticEvent, useEffect, useMemo, useRef } from "react";
import ReactDatePicker, { registerLocale } from "react-datepicker";
import { useTranslation } from "@/app/i18n/client";
import { MdCancel, MdRefresh } from "react-icons/md";
import { cn } from "@/lib/utils";
import { useDateLocale } from "@/app/[lng]/_components/datelocale-context";
import "react-datepicker/dist/react-datepicker.min.css";
import "@/style/react-datepicker.css";
export interface DateFormFieldProps {
  name: string;
  label?: string;
  onChange: DatePickerWithoutRangeOnChange;
  selected: Date | undefined;
  locale: string;
  icon?: React.ReactNode;
  error?: {
    show: boolean;
    text: string;
  };
  minDate?: Date;
  maxDate?: Date;
  isClearable?: boolean;
  isTodaySetable?: boolean;
  dontDisplayTime?: boolean;
  timeFormat?: string;
  dateFormat?: string;
  wrapperClassName?: string;
  children?: React.ReactNode;
  childClassName?: string;
  labelClassName?: string;
  noAutomaticReplacementDate?: boolean;
  open?: boolean;
  noOldFormClasses?: true;
  "data-sentry-unmask"?: boolean;
}

// eslint-disable-next-line import/no-unused-modules
export type DatePickerWithoutRangeOnChange = (date: Date | null | undefined, event: SyntheticEvent<unknown, Event> | undefined) => void;
export type DatePickerWithRangeOnChange = (date: Date | [Date, Date] | null, event: SyntheticEvent<unknown, Event> | undefined) => void;
export default function DateFormField(props) {
  const $ = _c(75);
  const {
    t,
    i18n
  } = useTranslation();
  let t0;
  if ($[0] !== props.minDate || $[1] !== props.name || $[2] !== props.selected) {
    const now = new Date();
    t0 = props.name === "dateOutward" ? props.selected?.getDate() === now.getDate() && props.selected?.getMonth() === now.getMonth() && props.selected?.getFullYear() === now.getFullYear() ? endOfDay(now) : startOfDay(now) : props.minDate && props.selected?.getDate() === props.minDate.getDate() && props.selected?.getMonth() === props.minDate.getMonth() && props.selected?.getFullYear() === props.minDate.getFullYear() ? endOfDay(now) : startOfDay(now);
    $[0] = props.minDate;
    $[1] = props.name;
    $[2] = props.selected;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const maxTime = t0;
  const locale = useDateLocale();
  let t1;
  let t2;
  if ($[4] !== i18n.language || $[5] !== locale) {
    t1 = () => {
      const loadLocale = async () => {
        if (locale) {
          registerLocale(i18n.language, locale);
        }
      };
      loadLocale();
    };
    t2 = [i18n.language, locale];
    $[4] = i18n.language;
    $[5] = locale;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  const reference = useRef(null);
  const dateFormat = props.dateFormat ? props.dateFormat : "dd.MM.yyyy HH:mm";
  const timeFormat = props.timeFormat ? props.timeFormat : "HH:mm";
  const t3 = !props.noOldFormClasses && "form-group";
  let t4;
  if ($[8] !== props.wrapperClassName || $[9] !== t3) {
    t4 = cn(props.wrapperClassName, t3);
    $[8] = props.wrapperClassName;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== props.label || $[12] !== props.labelClassName || $[13] !== props.name || $[14] !== props.noOldFormClasses) {
    t5 = props.label && props.noOldFormClasses && <label className={props.labelClassName} htmlFor={props.name}>{props.label}</label>;
    $[11] = props.label;
    $[12] = props.labelClassName;
    $[13] = props.name;
    $[14] = props.noOldFormClasses;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const t6 = !props.noOldFormClasses ? "input-group" : "relative flex w-full items-center";
  let t7;
  if ($[16] !== props.childClassName || $[17] !== t6) {
    t7 = cn(t6, props.childClassName);
    $[16] = props.childClassName;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== props.label || $[20] !== props.name || $[21] !== props.noOldFormClasses) {
    t8 = props.label && !props.noOldFormClasses && <label htmlFor={props.name}>{props.label}</label>;
    $[19] = props.label;
    $[20] = props.name;
    $[21] = props.noOldFormClasses;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== props.icon || $[24] !== props.noOldFormClasses) {
    t9 = props.icon && <span className={cn(props.noOldFormClasses && "scale-125 text-secondary", !props.noOldFormClasses && "icon-container")}>{props.icon}</span>;
    $[23] = props.icon;
    $[24] = props.noOldFormClasses;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const t10 = props.open;
  const t11 = props.name;
  const t12 = props.name;
  const t13 = props.selected;
  let t14;
  if ($[26] !== t) {
    t14 = t("MAIN.TRAVELCOMPANY.DATEINPUTPLACEHOLDER") ?? "";
    $[26] = t;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = props.onChange as DatePickerWithRangeOnChange;
  const t16 = props.dontDisplayTime ? undefined : true;
  let t17;
  if ($[28] !== t) {
    t17 = t("MAIN.TIME.TEXT") ?? "Time";
    $[28] = t;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  const t18 = props.minDate;
  let t19;
  if ($[30] !== props.minDate) {
    t19 = props.minDate || new Date();
    $[30] = props.minDate;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  const t20 = props.maxDate || maxTime;
  const t21 = props.noOldFormClasses && "top-[110%] right-auto left-0";
  let t22;
  if ($[32] !== t21) {
    t22 = cn(t21);
    $[32] = t21;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  const t23 = props.noOldFormClasses && "w-full";
  let t24;
  if ($[34] !== t23) {
    t24 = cn(t23);
    $[34] = t23;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  const t25 = props.noOldFormClasses && "w-full";
  const t26 = props["data-sentry-unmask"] && "sentry-unmask";
  let t27;
  if ($[36] !== t25 || $[37] !== t26) {
    t27 = cn(t25, t26);
    $[36] = t25;
    $[37] = t26;
    $[38] = t27;
  } else {
    t27 = $[38];
  }
  let t28;
  if ($[39] !== dateFormat || $[40] !== locale || $[41] !== props.minDate || $[42] !== props.name || $[43] !== props.open || $[44] !== props.selected || $[45] !== t14 || $[46] !== t15 || $[47] !== t16 || $[48] !== t17 || $[49] !== t19 || $[50] !== t20 || $[51] !== t22 || $[52] !== t24 || $[53] !== t27 || $[54] !== timeFormat) {
    t28 = <ReactDatePicker ref={reference} open={t10} name={t11} id={t12} selected={t13} placeholderText={t14} locale={locale} onChange={t15} showTimeSelect={t16} timeFormat={timeFormat} timeIntervals={15} timeCaption={t17} minDate={t18} dateFormat={dateFormat} minTime={t19} maxTime={t20} adjustDateOnChange={true} closeOnScroll={false} popperClassName={t22} calendarClassName={t24} className={t27} autoComplete="off" />;
    $[39] = dateFormat;
    $[40] = locale;
    $[41] = props.minDate;
    $[42] = props.name;
    $[43] = props.open;
    $[44] = props.selected;
    $[45] = t14;
    $[46] = t15;
    $[47] = t16;
    $[48] = t17;
    $[49] = t19;
    $[50] = t20;
    $[51] = t22;
    $[52] = t24;
    $[53] = t27;
    $[54] = timeFormat;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== props) {
    t29 = props.isTodaySetable && <div className="setToday"><button className="today-button bg-matchbus" onClick={e => props.onChange(new Date(), e)}><div className="icon"><MdRefresh /></div></button></div>;
    $[56] = props;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== props) {
    t30 = props.isClearable && <div className="options"><button className="pop-button" onClick={e_0 => props.onChange(undefined, e_0)}><div className="icon"><MdCancel /></div></button></div>;
    $[58] = props;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== t28 || $[61] !== t29 || $[62] !== t30 || $[63] !== t7 || $[64] !== t8 || $[65] !== t9) {
    t31 = <div className={t7}>{t8}{t9}{t28}{t29}{t30}</div>;
    $[60] = t28;
    $[61] = t29;
    $[62] = t30;
    $[63] = t7;
    $[64] = t8;
    $[65] = t9;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== props.error) {
    t32 = props.error && props.error.show && <span className="form-error">{props.error.text}</span>;
    $[67] = props.error;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== props.children || $[70] !== t31 || $[71] !== t32 || $[72] !== t4 || $[73] !== t5) {
    t33 = <div className={t4}>{t5}{t31}{t32}{props.children}</div>;
    $[69] = props.children;
    $[70] = t31;
    $[71] = t32;
    $[72] = t4;
    $[73] = t5;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  return t33;
}